<script setup>
/**
 * UpdateClientDetailsView
 * Form for agents to update existing client details
 */
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { getFirebaseDb } from '@/core/firebase/firebase'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'
import { useEmail } from '@/core/composables/useEmail'
import { generateUpdateClientEmailHtml } from '@/core/utils/emailTemplates'
import { getEmailRecipients } from '@/core/services/emailSections'
import { ROUTES } from '@/core/config/constants'

// Components
import FormField from '@/ui/components/common/FormField.vue'
import FormSelect from '@/ui/components/common/FormSelect.vue'
import AppButton from '@/ui/components/AppButton.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()
const { sendEmail } = useEmail()

// State
const clientNumber = ref('')
const clientData = ref(null)
const isLoading = ref(false)
const isSubmitting = ref(false)

// Form data
const formData = ref({
  client_name: '',
  business_id: '',
  address: '',
  city: '',
  address_comments: '',
  business_phone: '',
  mobile_phone: '',
  fax_number: '',
  contact_name: '',
  owner_full_name: '',
  owner_id: '',
  owner_birth_date: '',
  owner_address: '',
  business_type: '',
  price_list: '',
  business_seniority: 0,
  back_check: '',
  kupot_qnt: 0,
  kosher_type: '',
  business_migdar: '',
  payment_type: '',
  bank_name: '',
  bank_branche_number: '',
  bank_account_number: '',
  bank_account_name: '',
  credit_days: '',
  check_delivery: '',
  visit_days: '',
  visit_frequency: '',
  visit_frequency_twice_a_week: '',
  visit_frequency_once_a_month: '',
  visit_like_customer: '',
  is_visit_limit: '',
  visit_limit_hour_start: '',
  visit_limit_hour_end: '',
})

// Options for selects
const businessTypeOptions = [
  'מכולת/מעדנייה',
  'מינימרקט (קופה אחת)',
  'סופר מרקט (2 קופות ומעלה)',
  'פיצוצייה / דרגסטור',
  'אחר',
]

const priceListOptions = ['בסיס', 'פלטינום', 'זהב', 'סיטונאי', 'מחירון אישי']

const yesNoOptions = ['כן', 'לא']

const kosherTypeOptions = ['לא כשר', 'כשרות רגילה', 'כשרות גבוהה']

const businessMigdarOptions = ['רוסי', 'ישראלי', 'ערבי']

const paymentTypeOptions = ['מזומן', 'המחאה - שיק', 'העברה בנקאית']

const bankNameOptions = [
  'לאומי',
  'הפועלים',
  'דיסקונט',
  'מרכנתיל דיסקונט',
  'הבינלאומי',
  'מסד',
  'יהב',
  'הדואר',
  'ירושלים',
  'אחר',
]

const checkDeliveryOptions = ['סוכן', 'נהג']

const visitDaysOptions = ['א', 'ב', 'ג', 'ד', 'ה', 'ו']

const visitFrequencyOptions = ['פעם בשבוע', 'פעמיים בשבוע', 'פעם בחודש', 'כמו לקוח']

const visitFrequencyTwiceOptions = ['', '1 פלוס 3', '2 פלוס 4']

const visitFrequencyOnceOptions = ['', 'שבוע-1', 'שבוע-2', 'שבוע-3', 'שבוע-4']

// Computed
const agentName = computed(() => authStore.profile?.displayName || authStore.profile?.email || '')
const agentNumber = computed(() => authStore.profile?.agent || '')

const showBankFields = computed(
  () => formData.value.payment_type && formData.value.payment_type !== 'מזומן'
)
const showTwiceWeekOption = computed(() => formData.value.visit_frequency === 'פעמיים בשבוע')
const showOnceMonthOption = computed(() => formData.value.visit_frequency === 'פעם בחודש')
const showLikeCustomerField = computed(() => formData.value.visit_frequency === 'כמו לקוח')
const showVisitLimitHours = computed(() => formData.value.is_visit_limit === 'כן')

// Fetch client from database
async function fetchClient() {
  if (!clientNumber.value) {
    toast.error('יש להזין מספר לקוח')
    return
  }

  isLoading.value = true

  try {
    const db = getFirebaseDb()
    const clientRef = doc(db, 'ClientsV2', clientNumber.value)
    const clientSnap = await getDoc(clientRef)

    if (clientSnap.exists()) {
      clientData.value = clientSnap.data()
      // Populate form with existing data
      formData.value.client_name = clientData.value.name || clientData.value.client_name || ''
      // Copy other fields if they exist
      const fields = [
        'business_id',
        'address',
        'city',
        'address_comments',
        'business_phone',
        'mobile_phone',
        'fax_number',
        'contact_name',
        'owner_full_name',
        'owner_id',
        'owner_birth_date',
        'owner_address',
        'business_type',
        'price_list',
        'business_seniority',
        'back_check',
        'kupot_qnt',
        'kosher_type',
        'business_migdar',
        'payment_type',
        'bank_name',
        'bank_branche_number',
        'bank_account_number',
        'bank_account_name',
        'credit_days',
        'check_delivery',
        'visit_days',
        'visit_frequency',
        'visit_frequency_twice_a_week',
        'visit_frequency_once_a_month',
        'visit_like_customer',
        'is_visit_limit',
        'visit_limit_hour_start',
        'visit_limit_hour_end',
      ]
      fields.forEach((field) => {
        if (clientData.value[field] !== undefined) {
          formData.value[field] = clientData.value[field]
        }
      })
      toast.success(`לקוח ${clientNumber.value} נטען בהצלחה`)
    } else {
      toast.error(`לקוח מספר ${clientNumber.value} לא קיים במערכת`)
    }
  } catch (error) {
    console.error('Error fetching client:', error)
    toast.error('שגיאה בטעינת פרטי הלקוח')
  } finally {
    isLoading.value = false
  }
}

// Fill test data
function fillTestData() {
  const testValue = 'TEST - בדיקה'
  formData.value = {
    client_name: testValue,
    business_id: '123456789',
    address: testValue,
    city: testValue,
    address_comments: testValue,
    business_phone: '0312345678',
    mobile_phone: '0501234567',
    fax_number: '0312345679',
    contact_name: testValue,
    owner_full_name: testValue,
    owner_id: '123456789',
    owner_birth_date: '1990-01-01',
    owner_address: testValue,
    business_type: 'מכולת/מעדנייה',
    price_list: 'בסיס',
    business_seniority: 5,
    back_check: 'לא',
    kupot_qnt: 2,
    kosher_type: 'כשרות רגילה',
    business_migdar: 'ישראלי',
    payment_type: 'המחאה - שיק',
    bank_name: 'לאומי',
    bank_branche_number: '123',
    bank_account_number: '123456',
    bank_account_name: testValue,
    credit_days: 'שוטף 30',
    check_delivery: 'סוכן',
    visit_days: 'א',
    visit_frequency: 'פעם בשבוע',
    visit_frequency_twice_a_week: '',
    visit_frequency_once_a_month: '',
    visit_like_customer: '',
    is_visit_limit: 'לא',
    visit_limit_hour_start: '',
    visit_limit_hour_end: '',
  }
  toast.info('נתוני בדיקה הוזנו')
}

// Validation
function validate() {
  // Check if at least one field has value
  const hasValue = Object.entries(formData.value).some(([key, val]) => {
    if (key === 'business_seniority' || key === 'kupot_qnt') {
      return val > 0
    }
    return val !== '' && val !== null && val !== undefined
  })

  if (!hasValue) {
    toast.error('עליך למלא לפחות שדה אחד')
    return false
  }

  return true
}

// Submit handler
async function handleSubmit() {
  if (!validate()) return

  isSubmitting.value = true

  try {
    // Save update request to Firestore
    const db = getFirebaseDb()
    const updateRef = collection(db, 'Agent_portal', 'Agent_portal', 'Update_client')
    await addDoc(updateRef, {
      ...formData.value,
      clientNumber: clientNumber.value,
      agent_name: agentName.value,
      agent_number: agentNumber.value,
      createdAt: serverTimestamp(),
    })

    // Send email notification
    try {
      const emailHtml = generateUpdateClientEmailHtml(
        formData.value,
        {
          name: agentName.value,
          number: agentNumber.value,
        },
        clientNumber.value
      )

      const recipients = await getEmailRecipients('updateClientAgent')
      if (recipients.length > 0) {
        await sendEmail({
          to: recipients,
          subject: `עדכון לקוח: ${formData.value.client_name} (${clientNumber.value}) - סוכן ${agentName.value}`,
          html: emailHtml,
          from: 'GoFood פורטל סוכנים',
        })
      } else {
        toast.warning('לא הוגדרו כתובות מייל לשליחה')
      }
    } catch (emailError) {
      console.error('Failed to queue email:', emailError)
    }

    toast.success('העדכון נשלח בהצלחה!')

    // Navigate back
    setTimeout(() => {
      router.push({ name: ROUTES.AGENTS_PORTAL })
    }, 1500)
  } catch (error) {
    console.error('Submit error:', error)
    toast.error('אירעה שגיאה בשליחת העדכון')
  } finally {
    isSubmitting.value = false
  }
}

// Reset and select different client
function resetClient() {
  clientData.value = null
  clientNumber.value = ''
  // Reset form
  Object.keys(formData.value).forEach((key) => {
    if (key === 'business_seniority' || key === 'kupot_qnt') {
      formData.value[key] = 0
    } else {
      formData.value[key] = ''
    }
  })
}

function goBack() {
  router.push({ name: ROUTES.AGENTS_PORTAL })
}

// Auto-fetch client if navigated with clientMakat query param
onMounted(() => {
  const makat = route.query.clientMakat
  if (makat) {
    clientNumber.value = String(makat)
    fetchClient()
  }
})
</script>

<template>
  <div class="update-client-view">
    <!-- Header -->
    <div class="view-header">
      <button class="back-btn" @click="goBack">
        <span class="material-symbols-rounded">arrow_forward</span>
      </button>
      <h1 class="view-title">עדכון פרטי לקוח</h1>
      <button v-if="clientData" class="test-btn" @click="fillTestData">
        <span class="material-symbols-rounded">science</span>
        בדיקה
      </button>
    </div>

    <!-- Client Search Section -->
    <div v-if="!clientData" class="search-section">
      <div class="search-card">
        <span class="material-symbols-rounded search-icon">person_search</span>
        <h2 class="search-title">חיפוש לקוח</h2>
        <p class="search-desc">הזן מספר לקוח כדי לטעון את פרטיו</p>

        <div class="search-input-wrapper">
          <FormField
            v-model="clientNumber"
            label="מספר לקוח"
            placeholder="הזן מספר לקוח"
            type="tel"
            @keypress.enter="fetchClient"
          />
        </div>

        <AppButton
          variant="primary"
          :loading="isLoading"
          :disabled="isLoading || !clientNumber"
          class="search-btn"
          @click="fetchClient"
        >
          <span class="material-symbols-rounded">search</span>
          בחר לקוח
        </AppButton>
      </div>
    </div>

    <!-- Edit Form -->
    <div v-else class="form-content">
      <!-- Client Info Banner -->
      <div class="client-banner">
        <div class="client-info">
          <span class="material-symbols-rounded">store</span>
          <div>
            <p class="client-name">{{ formData.client_name || 'לקוח' }}</p>
            <p class="client-number">מספר לקוח: {{ clientNumber }}</p>
          </div>
        </div>
        <button class="change-client-btn" @click="resetClient">
          <span class="material-symbols-rounded">swap_horiz</span>
          החלף לקוח
        </button>
      </div>

      <!-- Form Sections -->
      <div class="form-sections">
        <!-- Business Details -->
        <section class="form-section">
          <h3 class="section-title">
            <span class="material-symbols-rounded">store</span>
            פרטי העסק
          </h3>
          <div class="section-fields">
            <FormField
              v-model="formData.client_name"
              label="שם הלקוח/בית העסק"
              placeholder="שם מלא"
            />
            <FormField v-model="formData.business_id" label="ח.פ או ע.מ" placeholder="ח.פ או ע.מ" />
            <FormField v-model="formData.address" label="כתובת" placeholder="רחוב, מספר" />
            <FormField v-model="formData.city" label="עיר" placeholder="עיר" />
            <FormField
              v-model="formData.address_comments"
              label="הערות לכתובת"
              placeholder="הערות"
            />
            <FormField
              v-model="formData.business_phone"
              type="tel"
              label="מספר טלפון בבית העסק"
              placeholder="מספר טלפון"
            />
            <FormField
              v-model="formData.mobile_phone"
              type="tel"
              label="מספר טלפון נייד"
              placeholder="מספר נייד"
            />
            <FormField
              v-model="formData.fax_number"
              type="tel"
              label="מספר פקס"
              placeholder="מספר פקס"
            />
            <FormField
              v-model="formData.contact_name"
              label="שם איש הקשר בעסק"
              placeholder="שם איש הקשר"
            />
          </div>
        </section>

        <!-- Owner Details -->
        <section class="form-section">
          <h3 class="section-title">
            <span class="material-symbols-rounded">person</span>
            פרטי בעל העסק
          </h3>
          <div class="section-fields">
            <FormField
              v-model="formData.owner_full_name"
              label="שם בעל העסק (פרטי + משפחה)"
              placeholder="שם מלא"
            />
            <FormField
              v-model="formData.owner_id"
              label="מס תז של בעל העסק"
              placeholder="מס זהות"
            />
            <FormField
              v-model="formData.owner_birth_date"
              type="date"
              label="תאריך לידה של בעל העסק"
            />
            <FormField
              v-model="formData.owner_address"
              label="כתובת מגורים של בעל העסק"
              placeholder="כתובת מלאה"
            />
          </div>
        </section>

        <!-- Business Type -->
        <section class="form-section">
          <h3 class="section-title">
            <span class="material-symbols-rounded">category</span>
            פרטי עסק נוספים
          </h3>
          <div class="section-fields">
            <FormSelect
              v-model="formData.business_type"
              label="סוג העסק"
              :options="businessTypeOptions"
              placeholder="בחר סוג עסק"
            />
            <FormSelect
              v-model="formData.price_list"
              label="מחירון לקוח"
              :options="priceListOptions"
              placeholder="בחר מחירון"
            />

            <div class="field-group">
              <label class="field-label">וותק העסק בשנים</label>
              <input
                v-model.number="formData.business_seniority"
                type="number"
                min="0"
                max="50"
                class="number-input"
              />
            </div>

            <FormSelect
              v-model="formData.back_check"
              label="האם חזרו ללקוח צ'קים בעבר?"
              :options="yesNoOptions"
              placeholder="בחר"
            />

            <div class="field-group">
              <label class="field-label">מספר קופות</label>
              <input
                v-model.number="formData.kupot_qnt"
                type="number"
                min="0"
                max="1000"
                class="number-input"
              />
            </div>

            <FormSelect
              v-model="formData.kosher_type"
              label="רמת כשרות"
              :options="kosherTypeOptions"
              placeholder="בחר רמת כשרות"
            />
            <FormSelect
              v-model="formData.business_migdar"
              label="מגדר עסקי"
              :options="businessMigdarOptions"
              placeholder="בחר מגדר עסקי"
            />
          </div>
        </section>

        <!-- Payment Details -->
        <section class="form-section">
          <h3 class="section-title">
            <span class="material-symbols-rounded">payments</span>
            פרטי תשלום
          </h3>
          <div class="section-fields">
            <FormSelect
              v-model="formData.payment_type"
              label="תנאי תשלום"
              :options="paymentTypeOptions"
              placeholder="בחר תנאי תשלום"
            />

            <template v-if="showBankFields">
              <FormSelect
                v-model="formData.bank_name"
                label="שם הבנק"
                :options="bankNameOptions"
                placeholder="בחר בנק"
              />
              <FormField
                v-model="formData.bank_branche_number"
                type="number"
                label="מספר סניף"
                placeholder="מספר סניף"
              />
              <FormField
                v-model="formData.bank_account_number"
                type="number"
                label="מספר חשבון"
                placeholder="מספר חשבון"
              />
              <FormField
                v-model="formData.bank_account_name"
                label="שם חשבון"
                placeholder="שם החשבון"
              />
            </template>

            <FormField
              v-model="formData.credit_days"
              label="ימי אשראי"
              placeholder="שוטף 60 / מזומן 30"
            />
            <FormSelect
              v-model="formData.check_delivery"
              label="למי תהייה מסירת הצ'ק?"
              :options="checkDeliveryOptions"
              placeholder="בחר"
            />
          </div>
        </section>

        <!-- Visit Details -->
        <section class="form-section">
          <h3 class="section-title">
            <span class="material-symbols-rounded">calendar_month</span>
            פירוט ביקורים
          </h3>
          <div class="section-fields">
            <FormSelect
              v-model="formData.visit_days"
              label="יום בשבוע לביקור סוכן"
              :options="visitDaysOptions"
              placeholder="בחר יום"
            />
            <FormSelect
              v-model="formData.visit_frequency"
              label="תדירות ביקורים"
              :options="visitFrequencyOptions"
              placeholder="בחר תדירות"
            />

            <FormField
              v-if="showLikeCustomerField"
              v-model="formData.visit_like_customer"
              type="tel"
              label="כמו לקוח מס:"
              placeholder="מספר לקוח"
            />
            <FormSelect
              v-if="showTwiceWeekOption"
              v-model="formData.visit_frequency_twice_a_week"
              label="סוג הביקור"
              :options="visitFrequencyTwiceOptions"
              placeholder="בחר"
            />
            <FormSelect
              v-if="showOnceMonthOption"
              v-model="formData.visit_frequency_once_a_month"
              label="סוג הביקור"
              :options="visitFrequencyOnceOptions"
              placeholder="בחר"
            />

            <FormSelect
              v-model="formData.is_visit_limit"
              label="האם שעות קבלת הסחורה מוגבלות?"
              :options="yesNoOptions"
              placeholder="בחר"
            />

            <div v-if="showVisitLimitHours" class="time-fields">
              <div class="time-field">
                <label class="field-label">משעה</label>
                <input v-model="formData.visit_limit_hour_start" type="time" class="time-input" />
              </div>
              <div class="time-field">
                <label class="field-label">עד שעה</label>
                <input v-model="formData.visit_limit_hour_end" type="time" class="time-input" />
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- Submit Button -->
      <div class="form-actions">
        <AppButton
          variant="primary"
          :loading="isSubmitting"
          :disabled="isSubmitting"
          @click="handleSubmit"
        >
          <span class="material-symbols-rounded">check</span>
          עדכון פרטי לקוח
        </AppButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.update-client-view {
  padding: var(--spacing-lg);
  max-width: 800px;
  margin: 0 auto;
}

.view-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.back-btn:hover {
  background: var(--bg-tertiary);
}

.view-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
  flex: 1;
}

.test-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px dashed var(--color-warning);
  background: rgba(245, 158, 11, 0.1);
  border-radius: var(--border-radius);
  color: var(--color-warning);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.test-btn:hover {
  background: rgba(245, 158, 11, 0.2);
}

.test-btn .material-symbols-rounded {
  font-size: 18px;
}

/* Search Section */
.search-section {
  display: flex;
  justify-content: center;
  padding: var(--spacing-xl) 0;
}

.search-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  text-align: center;
  max-width: 400px;
  width: 100%;
  box-shadow: var(--shadow-sm);
}

.search-icon {
  font-size: 48px;
  color: var(--color-primary);
  margin-bottom: var(--spacing-md);
}

.search-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.search-desc {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0 0 var(--spacing-lg);
}

.search-input-wrapper {
  margin-bottom: var(--spacing-md);
  text-align: right;
}

.search-btn {
  width: 100%;
}

/* Client Banner */
.client-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #283548 0%, #334155 100%);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md) var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  color: white;
}

.client-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.client-info .material-symbols-rounded {
  font-size: 32px;
  opacity: 0.9;
}

.client-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0;
}

.client-number {
  font-size: var(--font-size-sm);
  opacity: 0.9;
  margin: 0;
}

.change-client-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.change-client-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Form Sections */
.form-sections {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.form-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
}

.section-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0 0 var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 2px solid var(--color-primary);
}

.section-title .material-symbols-rounded {
  font-size: 22px;
}

.section-fields {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.number-input,
.time-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-base);
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.number-input:focus,
.time-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(40, 53, 72, 0.1);
}

.time-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
}

.time-field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

/* Form Actions */
.form-actions {
  margin-top: var(--spacing-xl);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--border-color);
}

.form-actions .app-button {
  width: 100%;
}

/* Mobile */
@media (max-width: 600px) {
  .update-client-view {
    padding: var(--spacing-md);
    padding-bottom: calc(var(--spacing-md) + 80px);
  }

  .view-title {
    font-size: var(--font-size-lg);
  }

  .client-banner {
    flex-direction: column;
    gap: var(--spacing-md);
    text-align: center;
  }

  .client-info {
    flex-direction: column;
  }

  .form-section {
    padding: var(--spacing-md);
  }

  .time-fields {
    grid-template-columns: 1fr;
  }

  .form-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: var(--spacing-md);
    background: var(--bg-primary);
    border-top: 1px solid var(--border-color);
    box-shadow: var(--shadow-lg);
    margin-top: 0;
    z-index: 100;
  }
}
</style>
